import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const Services = () => {
    const services = [
        {
            title: 'Residential Design',
            description: 'Transform your home into a personalized sanctuary with our comprehensive residential interior design services.',
            features: [
                'Space Planning & Layout',
                'Color & Material Selection',
                'Custom Furniture Design',
                'Lighting Design',
                'Art & Accessories Curation',
            ],
            image: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0',
        },
        {
            title: 'Commercial Design',
            description: 'Create impactful commercial spaces that enhance brand identity and user experience.',
            features: [
                'Retail Store Design',
                'Restaurant & Hospitality',
                'Showroom Design',
                'Brand Integration',
                'Traffic Flow Optimization',
            ],
            image: 'https://images.unsplash.com/photo-1537726235470-8504e3beef77',
        },
        {
            title: 'Corporate Design',
            description: 'Design productive and inspiring workplaces that reflect your company culture.',
            features: [
                'Office Space Planning',
                'Ergonomic Solutions',
                'Collaborative Areas',
                'Meeting Room Design',
                'Acoustic Solutions',
            ],
            image: 'https://images.unsplash.com/photo-1497366216548-37526070297c',
        },
    ];

    const processSteps = [
        {
            number: '01',
            title: 'Consultation',
            description: 'Initial meeting to understand your vision, requirements, and budget.',
        },
        {
            number: '02',
            title: 'Concept Development',
            description: 'Creating detailed design concepts and mood boards for your approval.',
        },
        {
            number: '03',
            title: 'Design Development',
            description: 'Refining the selected concept with detailed specifications and plans.',
        },
        {
            number: '04',
            title: 'Implementation',
            description: 'Coordinating with contractors and vendors to bring the design to life.',
        },
    ];

    return (
        <div className="min-h-screen bg-light">
            {/* Hero Section */}
            <section className="relative py-24 bg-primary text-light">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        className="text-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h1 className="font-serif text-5xl font-bold mb-6">Our Services</h1>
                        <p className="text-xl text-light/80 max-w-2xl mx-auto">
                            Comprehensive interior design solutions tailored to your unique needs and vision
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                        {services.map((service, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.2 }}
                                className="bg-white rounded-2xl overflow-hidden shadow-xl"
                            >
                                <div className="h-64 relative">
                                    <img
                                        src={service.image}
                                        alt={service.title}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent">
                                        <div className="absolute bottom-6 left-6">
                                            <h3 className="font-serif text-2xl font-bold text-light mb-2">
                                                {service.title}
                                            </h3>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <p className="text-secondary mb-6">{service.description}</p>
                                    <ul className="space-y-3">
                                        {service.features.map((feature, i) => (
                                            <li key={i} className="flex items-center text-primary">
                                                <span className="text-accent mr-2">✓</span>
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Process Section */}
            <section className="py-24 bg-primary text-light">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="font-serif text-4xl font-bold mb-6">Our Process</h2>
                        <p className="text-xl text-light/80 max-w-2xl mx-auto">
                            A systematic approach to bringing your vision to life
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {processSteps.map((step, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.2 }}
                                className="relative"
                            >
                                <div className="bg-secondary/20 rounded-xl p-6 backdrop-blur-sm">
                                    <span className="font-serif text-4xl font-bold text-accent mb-4 block">
                                        {step.number}
                                    </span>
                                    <h3 className="font-serif text-xl font-bold mb-3">{step.title}</h3>
                                    <p className="text-light/80">{step.description}</p>
                                </div>
                                {index < processSteps.length - 1 && (
                                    <div className="hidden lg:block absolute top-1/2 right-0 w-full h-0.5 bg-accent/20 transform translate-x-1/2">
                                        <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2">
                                            <span className="block w-3 h-3 bg-accent rotate-45"></span>
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 bg-accent text-light text-center">
                <div className="max-w-3xl mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="font-serif text-4xl font-bold mb-6">
                            Ready to Start Your Project?
                        </h2>
                        <p className="text-xl mb-8">
                            Let's discuss how we can transform your space into something extraordinary
                        </p>
                        <Link
                            to="/contact"
                            className="btn-primary bg-light text-primary hover:bg-light/90"
                        >
                            Schedule a Consultation
                        </Link>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Services; 